import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import LoginCardContainer from '../components/Common/login-register/LoginCardContainer';
import LogoSection from '../components/Common/login-register/LogoSection';
import TitleSection from '../components/Common/TitleSection';
import EmailInputSection from '../components/Common/login-register/EmailInputSection';
import PhoneInputSection from '../components/Common/login-register/PhoneInputSection';
import PasswordInputSection from '../components/Common/login-register/PasswordInputSection';
import SignUpSection from '../components/Common/login-register/SignUpSection';
import DividerWithText from '../components/Common/login-register/DividerWithText';
import SocialLoginSection from '../components/Common/login-register/SocialLoginSection';
import FooterLinksSection from '../components/Common/login-register/FooterLinksSection';
import SignUpCardContent from '../components/Common/login-register/SignUpCardContent';
import OTPVerificationCardContent from '../components/Common/login-register/OTPVerificationCardContent';
import UserInfoCardContent from '../components/Common/UserInfoCardContent';

const validateEmail = (email) => /\S+@\S+\.\S+/.test(email);
const validatePhone = (phone) => /^1\d{9}$/.test(phone); // BD: 11 digit, starts with 1 (without +880)

const OTP_RESEND_SECONDS = 299; // 4:59

const LoginRegister = () => {
  const [searchParams] = useSearchParams();
  const modeParam = searchParams.get('mode'); // 'login' or 'register'
  
  // Determine background image
  // Only show login-page-bg.jpg for email sign in first page, otherwise green-background.jpg
  const [authStep, setAuthStep] = useState(modeParam === 'register' ? 'signup' : 'login');
  const [step, setStep] = useState('email');
  const [mode, setMode] = useState('email');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState('');
  const [resendTimer, setResendTimer] = useState(OTP_RESEND_SECONDS);
  const timerRef = useRef();

  // User info states
  const [name, setName] = useState('');
  const [gender, setGender] = useState('male');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // OTP resend timer effect
  useEffect(() => {
    if (authStep === 'otp' && resendTimer > 0) {
      timerRef.current = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    }
    return () => clearTimeout(timerRef.current);
  }, [authStep, resendTimer]);

  const handleNextEmail = () => {
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError('');
    setStep('password');
  };

  const handleNextPhone = () => {
    if (!validatePhone(phone.replace(/^\+?880/, ''))) {
      setPhoneError('Please enter a valid phone number');
      return;
    }
    setPhoneError('');
    setStep('password');
  };

  // Sign Up Card Next (with validation)
  const handleSignUpNext = () => {
    if (mode === 'phone') {
      if (!validatePhone(phone.replace(/^\+?880/, ''))) {
        setPhoneError('Please enter a valid phone number');
        return;
      }
      setPhoneError('');
    } else {
      if (!validateEmail(email)) {
        setEmailError('Please enter a valid email address');
        return;
      }
      setEmailError('');
    }
    setAuthStep('otp');
    setOtpValue('');
    setOtpError('');
    setResendTimer(OTP_RESEND_SECONDS);
  };

  // OTP Next (submit)
  const handleOtpNext = () => {
    if (!otpValue) {
      setOtpError('OTP field cannot be empty');
      return;
    }
    setOtpError('');
    setAuthStep('userinfo');
  };

  // Resend OTP
  const handleResendOtp = () => {
    setResendTimer(OTP_RESEND_SECONDS);
    setOtpValue('');
    setOtpError('');
    // এখানে OTP রিসেন্ড API কল করতে পারেন
  };

  // Mode switchers for signup card
  const handleSignUpPhoneClick = () => {
    setMode('phone');
    setEmailError('');
    setPhoneError('');
    setEmail('');
    setPhone('');
  };
  const handleSignUpEmailClick = () => {
    setMode('email');
    setEmailError('');
    setPhoneError('');
    setEmail('');
    setPhone('');
  };

  // Determine background image
  let bgImage = "/jpg/green-background.jpg";
  if (authStep === 'login' && step === 'email' && mode === 'email') {
    bgImage = "/jpg/login-page-bg.jpg";
  }

  // Render card content based on authStep
  let cardContent;
  if (authStep === 'userinfo') {
    cardContent = (
      <LoginCardContainer>
        <UserInfoCardContent
          name={name}
          onNameChange={e => setName(e.target.value)}
          gender={gender}
          onGenderChange={setGender}
          password={password}
          onPasswordChange={e => setPassword(e.target.value)}
          confirmPassword={confirmPassword}
          onConfirmPasswordChange={e => setConfirmPassword(e.target.value)}
          passwordError={passwordError}
          onSignUp={() => {}}
        />
      </LoginCardContainer>
    );
  } else if (authStep === 'otp') {
    cardContent = (
      <LoginCardContainer>
        <OTPVerificationCardContent
          mode={mode}
          phone={phone}
          email={email}
          otpValue={otpValue}
          onOtpChange={e => { setOtpValue(e.target.value); if (otpError) setOtpError(''); }}
          resendTimer={resendTimer}
          onResend={handleResendOtp}
          onNext={handleOtpNext}
        />
        {otpError && <Box color="red.500" textAlign="center" mt={2} fontSize="sm">{otpError}</Box>}
      </LoginCardContainer>
    );
  } else if (authStep === 'signup') {
    cardContent = (
      <LoginCardContainer>
        <SignUpCardContent
          mode={mode}
          value={mode === 'phone' ? phone : email}
          onChange={val => {
            if (mode === 'phone') {
              setPhone(val.replace(/\D/g, ''));
              if (phoneError) setPhoneError('');
            } else {
              setEmail(val.target ? val.target.value : val);
              if (emailError) setEmailError('');
            }
          }}
          error={mode === 'phone' ? phoneError : emailError}
          onNext={handleSignUpNext}
          onSignIn={() => setAuthStep('login')}
          onPhoneClick={handleSignUpPhoneClick}
          onEmailClick={handleSignUpEmailClick}
        />
      </LoginCardContainer>
    );
  } else {
    // Default login card
    cardContent = (
      <LoginCardContainer>
        <LogoSection />
        <TitleSection />
        {step === 'email' && mode === 'email' && (
          <EmailInputSection
            value={email}
            onChange={e => {
              setEmail(e.target.value);
              if (emailError) setEmailError('');
            }}
            error={emailError}
            onNext={handleNextEmail}
          />
        )}
        {step === 'email' && mode === 'phone' && (
          <PhoneInputSection
            value={phone}
            onChange={val => {
              setPhone(val.replace(/\D/g, ''));
              if (phoneError) setPhoneError('');
            }}
            error={phoneError}
            onNext={handleNextPhone}
          />
        )}
        {step === 'password' && <PasswordInputSection />}
        <SignUpSection onSignUp={() => setAuthStep('signup')} />
        <DividerWithText text="Or sign in with" />
        <SocialLoginSection
          mode={mode}
          onPhoneClick={() => { setMode('phone'); setStep('email'); }}
          onEmailClick={() => { setMode('email'); setStep('email'); }}
        />
        <FooterLinksSection />
      </LoginCardContainer>
    );
  }

  return (
    <Box
      minH="100vh"
      bgImage={`url('${bgImage}')`}
      bgSize="cover"
      bgPosition="center"
      bgRepeat="no-repeat"
      position="relative"
    >
      {/* Overlay for better readability */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="rgba(0, 0, 0, 0.4)"
        zIndex={1}
      />
      <Box position="relative" zIndex={2} minH="100vh" display="flex" alignItems="center" justifyContent="center">
        {cardContent}
      </Box>
    </Box>
  );
};

export default LoginRegister; 